import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import {
  ArrowRight, Mail, Phone, MapPin, GraduationCap, Scale, Briefcase, Globe,
  Menu, X, CheckCircle2, Building2,
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';


const NAV_ITEMS = [
  { id: 'home',         label: 'Home' },
  { id: 'about',        label: 'About' },
  { id: 'application',  label: 'Application' },
  { id: 'program',      label: 'Program' },
  { id: 'partnerships', label: 'Partnerships' },
  { id: 'faq',          label: 'FAQs' },
];

export default function LandingPage() {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('home');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [config, setConfig] = useState(null);
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeIntake, setActiveIntake] = useState(null);
  const observerRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const [configRes, faqsRes, intakeRes] = await Promise.all([
          axios.get(`${API}/site-config`),
          axios.get(`${API}/faqs`),
          axios.get(`${API}/intake-periods/active`).catch(() => ({ data: null })),
        ]);
        setConfig(configRes.data);
        setFaqs(faqsRes.data);
        setActiveIntake(intakeRes.data);
      } catch {
        setConfig(defaultConfig());
        setFaqs(defaultFaqs());
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // IntersectionObserver for active section highlight
  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter(e => e.isIntersecting);
        if (visible.length > 0) {
          // Pick the one closest to the top of viewport
          visible.sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
          setActiveSection(visible[0].target.id);
        }
      },
      { rootMargin: '-20% 0px -60% 0px', threshold: 0 }
    );
    NAV_ITEMS.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observerRef.current.observe(el);
    });
    return () => observerRef.current?.disconnect();
  }, [loading]);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMobileMenuOpen(false);
  };

  const img = (dataKey, urlKey) => config?.[dataKey] || config?.[urlKey] || '';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl font-playfair">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">

      {/* ─── Sticky Nav ─────────────────────────────────────────── */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-24">

            {/* Logo */}
            <img
              src={img('logo_data', 'logo_url')}
              alt="FB Attorneys Academy"
              className="cursor-pointer"
              style={{ height: '4.5rem' }}
              onClick={() => scrollTo('home')}
            />

            {/* Desktop nav */}
            <div className="hidden md:flex items-center space-x-6">
              {NAV_ITEMS.map(({ id, label }) => (
                <button
                  key={id}
                  onClick={() => scrollTo(id)}
                  className={`text-sm font-medium transition-colors hover:text-primary ${
                    activeSection === id ? 'text-primary border-b-2 border-primary pb-0.5' : 'text-gray-600'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            {/* Desktop actions */}
            <div className="hidden md:flex items-center space-x-4">
              <button onClick={() => navigate('/apply')} className="btn-primary text-sm">
                Apply Now
              </button>
            </div>

            {/* Hamburger */}
            <button
              className="md:hidden p-2 rounded-md text-gray-600 hover:text-primary hover:bg-gray-100"
              onClick={() => setMobileMenuOpen(o => !o)}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile dropdown */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-gray-200 bg-white/95 backdrop-blur-sm">
            <div className="px-4 py-4 space-y-1">
              {NAV_ITEMS.map(({ id, label }) => (
                <button
                  key={id}
                  onClick={() => scrollTo(id)}
                  className={`block w-full text-left px-3 py-3 rounded-md text-sm font-medium transition-colors ${
                    activeSection === id ? 'text-primary bg-gray-100' : 'text-gray-600 hover:text-primary hover:bg-gray-50'
                  }`}
                >
                  {label}
                </button>
              ))}
              <div className="pt-3 border-t border-gray-100 space-y-2">
                <button
                  onClick={() => { navigate('/apply'); setMobileMenuOpen(false); }}
                  className="btn-primary w-full text-center text-sm"
                >
                  Apply Now
                </button>
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* ─── Hero ───────────────────────────────────────────────── */}
      <section id="home" className="relative h-screen flex items-center justify-center noise-overlay">
        {/* Desktop hero image (landscape) */}
        <div
          className="absolute inset-0 z-0 hidden md:block"
          style={{
            backgroundImage: `url(${img('hero_image_data', 'hero_image_url')})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <div className="hero-overlay absolute inset-0" />
        </div>
        {/* Mobile hero image (portrait) — falls back to desktop image if not set */}
        <div
          className="absolute inset-0 z-0 block md:hidden"
          style={{
            backgroundImage: `url(${img('hero_image_mobile_data', 'hero_image_mobile_url') || img('hero_image_data', 'hero_image_url')})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center top',
          }}
        >
          <div className="hero-overlay absolute inset-0" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {activeIntake && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="mb-6"
            >
              <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm border border-white/30 rounded-full px-5 py-2 text-white text-sm">
                <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                <span>
                  <strong>{activeIntake.name}</strong> — Applications Open
                  {activeIntake.applications_close_date && (
                    <span className="ml-1 text-gray-300">
                      · Deadline: {new Date(activeIntake.applications_close_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                    </span>
                  )}
                </span>
              </div>
            </motion.div>
          )}

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-5xl md:text-7xl font-playfair font-bold mb-6"
            style={{ letterSpacing: '-0.02em', color: '#4897a0' }}
          >
            {config?.hero_title}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl md:text-2xl text-gray-200 mb-10 max-w-3xl mx-auto"
          >
            {config?.hero_subtitle}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.35 }}
          >
            <button
              onClick={() => navigate('/apply')}
              className="bg-secondary text-white px-10 py-4 rounded-sm text-lg font-medium hover:bg-secondary/90 transition-all inline-flex items-center gap-2"
            >
              {config?.hero_button_text}
              <ArrowRight className="w-5 h-5" />
            </button>
          </motion.div>
        </div>

        {/* Scroll cue */}
        <motion.div
          className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/60"
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
        >
          <ArrowRight className="w-5 h-5 rotate-90" />
        </motion.div>
      </section>

      {/* ─── About ──────────────────────────────────────────────── */}
      <section id="about" className="py-8 md:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-16 items-start">

            {/* Left: sticky image */}
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="md:sticky md:top-28"
            >
              <img
                src={img('about_image_data', 'about_image_url')}
                alt="FB Attorneys Academy"
                className="rounded-sm shadow-lg w-full object-cover"
              />
            </motion.div>

            {/* Right: accordion sub-sections */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <p className="text-xs font-semibold uppercase tracking-widest text-secondary mb-3">About</p>
              <h2 className="text-4xl md:text-5xl font-playfair font-bold text-primary mb-8">
                {config?.about_title}
              </h2>

              <Accordion type="multiple" defaultValue={['about-1']} className="space-y-3">
                <AccordionItem value="about-1" className="border border-gray-200 rounded-sm px-5">
                  <AccordionTrigger className="text-left font-semibold text-primary hover:no-underline py-4">
                    The Academy
                  </AccordionTrigger>
                  <AccordionContent className="pb-5 space-y-3">
                    <p className="text-gray-600 leading-relaxed">FB Attorneys Academy was established to close the widening gap between traditional legal education and the technological realities reshaping global legal practice. We believe excellence deserves access.</p>
                    <p className="text-gray-600 leading-relaxed">{config?.about_paragraph_1}</p>
                    <p className="text-gray-600 leading-relaxed italic">FB Attorneys Academy is a fully merit-based legal and technology hub admitting only high-potential legal professionals who demonstrate excellence, leadership, and commitment to advancing the future of law. Earn your place. Apply for consideration.</p>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="about-2" className="border border-gray-200 rounded-sm px-5">
                  <AccordionTrigger className="text-left font-semibold text-primary hover:no-underline py-4">
                    Access &amp; Excellence
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600 pb-5 leading-relaxed">
                    {config?.about_paragraph_2}
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="about-3" className="border border-gray-200 rounded-sm px-5">
                  <AccordionTrigger className="text-left font-semibold text-primary hover:no-underline py-4">
                    Academy Standard
                  </AccordionTrigger>
                  <AccordionContent className="pb-5">
                    <p className="text-gray-600 text-sm mb-3">To preserve quality and impact:</p>
                    <ul className="space-y-1.5 mb-3">
                      {['Cohorts are intentionally small', 'Candidates undergo structured review', 'Selection is merit-driven', 'Participation requires full commitment'].map((item, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm text-gray-600">
                          <span className="w-1.5 h-1.5 rounded-full bg-secondary flex-shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                    <p className="text-gray-600 text-sm font-medium">Excellence is our minimum standard.</p>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="about-4" className="border border-gray-200 rounded-sm px-5">
                  <AccordionTrigger className="text-left font-semibold text-primary hover:no-underline py-4">
                    Program Overview
                  </AccordionTrigger>
                  <AccordionContent className="pb-5 space-y-3">
                    <p className="text-gray-600 text-sm leading-relaxed">{config?.program_subtitle}</p>
                    <p className="text-gray-600 text-sm">Upon successful completion, you will be able to:</p>
                    <ul className="space-y-1.5">
                      {[
                        'Perform essential legal tasks with confidence, including drafting standard documents, conducting client-ready legal research and managing basic case files',
                        'Navigate core litigation and transactional procedures, demonstrating an understanding of court processes and alternative dispute resolution mechanisms',
                        'Utilise key legal technology tools to enhance efficiency in research, document management, and client service',
                        'Apply professional ethical rules and practical judgment in simulated real-world scenarios',
                        'Demonstrate commercial awareness and professional communication skills expected in a modern legal practice',
                        'Understand the ecosystem of legal institutions, including courts, tribunals, regulatory bodies and professional associations relevant to legal practice',
                      ].map((item, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                          <span className="w-1.5 h-1.5 rounded-full bg-secondary flex-shrink-0 mt-1.5" />
                          {item}
                        </li>
                      ))}
                    </ul>
                    <p className="text-gray-600 text-sm leading-relaxed">To ensure a high-quality, interactive learning experience, each cohort of the FB Attorneys Academy is limited to a small number of participants. Admission is competitive and based on academic merit, aptitude test performance, cohort balance, programme capacity and overall suitability for intensive practical training. Only shortlisted candidates who demonstrate strong potential, professionalism and commitment to legal practice will be admitted.</p>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>

              <div className="mt-8">
                <button
                  onClick={() => scrollTo('application')}
                  className="inline-flex items-center gap-2 text-secondary font-semibold hover:gap-3 transition-all"
                >
                  How to apply <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ─── Application & Selection ────────────────────────────── */}
      <section id="application" className="py-8 md:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-16 items-start">

            {/* Left: accordion */}
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <p className="text-xs font-semibold uppercase tracking-widest text-secondary mb-3">Apply</p>
              <h2 className="text-4xl md:text-5xl font-playfair font-bold text-primary mb-8">
                Application &amp; Selection
              </h2>

              <Accordion type="multiple" defaultValue={['sel-1']} className="space-y-3">
                <AccordionItem value="sel-1" className="border border-gray-200 rounded-sm px-5">
                  <AccordionTrigger className="text-left font-semibold text-primary hover:no-underline py-4">
                    Why Apply?
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600 pb-5 leading-relaxed">
                    If you are committed to innovation, leadership, and excellence in modern legal practice, FB Attorneys Academy is the right fit for you.
                    Our merit-based scholarship programme admits outstanding candidates and bridges the gap between technology, business development and legal practice.
                    The Academy is a next generation legal and technology hub designed to equip lawyers and legal professionals with the practical, business and technological skills required to lead.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="sel-2" className="border border-gray-200 rounded-sm px-5">
                  <AccordionTrigger className="text-left font-semibold text-primary hover:no-underline py-4">
                    Selection Philosophy
                  </AccordionTrigger>
                  <AccordionContent className="pb-5">
                    <p className="text-gray-600 text-sm mb-3 leading-relaxed">We admit only candidates who demonstrate:</p>
                    <ul className="space-y-1.5 mb-3">
                      {['Academic excellence', 'Leadership potential', 'Intellectual curiosity', 'Professional discipline', 'Commitment to ethical legal practice', 'Vision for innovation in law'].map((item, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm text-gray-600">
                          <span className="w-1.5 h-1.5 rounded-full bg-secondary flex-shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                    <p className="text-gray-600 text-sm italic">This is not an open enrolment programme. It is a merit-based academy.</p>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="sel-3" className="border border-gray-200 rounded-sm px-5">
                  <AccordionTrigger className="text-left font-semibold text-primary hover:no-underline py-4">
                    Application Process
                  </AccordionTrigger>
                  <AccordionContent className="pb-5">
                    <ol className="space-y-3">
                      {[
                        'Online application submission.',
                        'Academic & credentials review.',
                        'Shortlisting of candidates.',
                        'Online assessment.',
                        'Final selection notification.',
                      ].map((step, i) => (
                        <li key={i} className="flex items-start gap-3 text-gray-600 text-sm">
                          <span className="flex-shrink-0 w-6 h-6 rounded-full bg-secondary/10 text-secondary font-bold text-xs flex items-center justify-center mt-0.5">
                            {i + 1}
                          </span>
                          {step}
                        </li>
                      ))}
                    </ol>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="sel-4" className="border border-gray-200 rounded-sm px-5">
                  <AccordionTrigger className="text-left font-semibold text-primary hover:no-underline py-4">
                    Eligibility Criteria
                  </AccordionTrigger>
                  <AccordionContent className="pb-5">
                    <ul className="space-y-2.5">
                      {(config?.eligibility_criteria || []).filter(c => c.active !== false).map((c, i) => (
                        <li key={i} className="flex items-start gap-2.5 text-gray-600 text-sm">
                          <CheckCircle2 className="w-4 h-4 text-secondary flex-shrink-0 mt-0.5" />
                          {c.text}
                        </li>
                      ))}
                    </ul>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="sel-5" className="border border-gray-200 rounded-sm px-5">
                  <AccordionTrigger className="text-left font-semibold text-primary hover:no-underline py-4">
                    How to Apply
                  </AccordionTrigger>
                  <AccordionContent className="pb-5">
                    <p className="text-gray-600 text-sm mb-3 leading-relaxed">
                      The application process is simple and fully online. Follow these steps:
                    </p>
                    <ol className="space-y-2 mb-4">
                      {[
                        'Ensure you meet all the eligibility criteria listed below.',
                        'Have your academic transcripts, LLB completion/GPA proof, and a copy of your NIDA ID ready for upload.',
                        'Click the "Apply Now" button to access our secure online application portal.',
                        'Fill in all required details, upload your documents, and submit. You will receive an email confirmation upon successful submission.',
                      ].map((step, i) => (
                        <li key={i} className="flex items-start gap-3 text-gray-600 text-sm">
                          <span className="flex-shrink-0 w-6 h-6 rounded-full bg-secondary/10 text-secondary font-bold text-xs flex items-center justify-center mt-0.5">
                            {i + 1}
                          </span>
                          {step}
                        </li>
                      ))}
                    </ol>
                    <button
                      onClick={() => navigate('/apply')}
                      className="btn-primary text-sm inline-flex items-center gap-2"
                    >
                      Start Application <ArrowRight className="w-4 h-4" />
                    </button>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </motion.div>

            {/* Right: CTA card */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="md:sticky md:top-28 space-y-6"
            >
              <div className="bg-primary rounded-sm p-8 text-white">
                <p className="text-xs font-semibold uppercase tracking-widest text-secondary mb-2">
                  {activeIntake ? 'Now Open' : 'Applications'}
                </p>
                <h3 className="text-2xl font-playfair font-bold mb-3">
                  {activeIntake ? activeIntake.name : 'FB Attorneys Academy'}
                </h3>
                {activeIntake?.applications_close_date && (
                  <p className="text-gray-300 text-sm mb-5">
                    Application deadline: {new Date(activeIntake.applications_close_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                  </p>
                )}
                <button
                  onClick={() => navigate('/apply')}
                  className="w-full bg-secondary text-white py-3 rounded-sm font-medium hover:bg-secondary/90 transition-all mt-3"
                >
                  Apply Now
                </button>
              </div>

            </motion.div>
          </div>
        </div>
      </section>

      {/* ─── Program Structure ──────────────────────────────────── */}
      <section id="program" className="py-8 md:py-16 bg-muted">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <p className="text-xs font-semibold uppercase tracking-widest text-secondary mb-3">Curriculum</p>
            <h2 className="text-4xl md:text-5xl font-playfair font-bold text-primary mb-3">
              {config?.program_title}
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">{config?.program_subtitle}</p>
          </div>

          {/* Duration at a glance */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
            {[
              { label: 'Duration', value: '4 Weeks' },
              { label: 'Total Hours', value: '80 Hours' },
              { label: 'Weekly Commitment', value: 'Min 20 hrs/week' },
              { label: 'Format', value: 'Live + Independent' },
            ].map((stat, i) => (
              <div key={i} className="bg-white border border-gray-200 rounded-sm p-5 text-center">
                <p className="text-2xl font-playfair font-bold text-secondary mb-1">{stat.value}</p>
                <p className="text-xs text-gray-500 uppercase tracking-wide">{stat.label}</p>
              </div>
            ))}
          </div>

          {/* Modules */}
          <div className="grid md:grid-cols-3 gap-6 mb-10">
            {(config?.program_modules || []).map((mod, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                viewport={{ once: true }}
                className="bg-white border border-gray-200 p-7 rounded-sm"
              >
                <div className="text-3xl font-bold text-secondary/30 mb-3 font-playfair">{mod.number}</div>
                <h3 className="text-lg font-bold text-primary mb-4">{mod.title}</h3>
                <ul className="space-y-2">
                  {(mod.items || []).map((item, j) => (
                    <li key={j} className="flex items-center gap-2 text-sm text-gray-600">
                      <span className="w-1.5 h-1.5 rounded-full bg-secondary flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>

          {/* Weekly Structure & Delivery Mode */}
          <div className="grid md:grid-cols-2 gap-6 mt-2">
            <div className="bg-white border border-gray-200 rounded-sm p-7">
              <h3 className="text-lg font-bold text-primary mb-4">Weekly Structure</h3>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-24 text-xs font-semibold text-secondary uppercase tracking-wide pt-0.5">Morning</div>
                  <div>
                    <p className="text-sm text-gray-600">2 hours — Lecture/demo and questions and answers</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-24 text-xs font-semibold text-secondary uppercase tracking-wide pt-0.5">Afternoon</div>
                  <div>
                    <p className="text-sm text-gray-600">2 hours — Hands-on workshop/simulation/drafting lab</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white border border-gray-200 rounded-sm p-7">
              <h3 className="text-lg font-bold text-primary mb-4">Delivery Mode</h3>
              <ul className="space-y-2">
                {[
                  'In-person at law firm',
                  'Led by firm partners and senior associates',
                  'Guest practitioners from specialized fields from all over the world',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                    <span className="w-1.5 h-1.5 rounded-full bg-secondary flex-shrink-0 mt-1.5" />
                    {item}
                  </li>
                ))}
              </ul>
              <p className="text-xs text-gray-400 mt-4 italic">*Modules may be modified.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Partnerships & Affiliations ────────────────────────── */}
      <section id="partnerships" className="py-8 md:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <p className="text-xs font-semibold uppercase tracking-widest text-secondary mb-3">Network</p>
              <h2 className="text-4xl md:text-5xl font-playfair font-bold text-primary mb-5">
                {config?.partnerships_title || 'Partnerships & Affiliations'}
              </h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                {config?.partnerships_description}
              </p>
              <p className="text-gray-600 leading-relaxed mb-8 font-medium text-sm">We actively collaborate with:</p>
              <div className="grid grid-cols-2 gap-3">
                {(config?.partnerships_categories || []).map((cat, i) => (
                  <span key={i} className="inline-flex items-center gap-1.5 bg-primary/5 border border-primary/15 text-primary text-sm px-4 py-2 rounded-full">
                    <Building2 className="w-3.5 h-3.5 text-secondary flex-shrink-0" />
                    {cat}
                  </span>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <div className="border border-gray-200 rounded-sm p-6">
                <h4 className="font-semibold text-primary mb-3 text-sm">These partnerships enhance:</h4>
                <ul className="space-y-2">
                  {['Learning relevance', 'Industry exposure', 'Professional opportunities for scholars'].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-gray-600">
                      <span className="w-1.5 h-1.5 rounded-full bg-secondary flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <p className="text-gray-600 leading-relaxed text-sm">We continue to expand our network to support the next generation of legal leaders.</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ─── FAQs ───────────────────────────────────────────────── */}
      <section id="faq" className="py-8 md:py-16 bg-muted">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <p className="text-xs font-semibold uppercase tracking-widest text-secondary mb-3">FAQs</p>
            <h2 className="text-4xl md:text-5xl font-playfair font-bold text-primary mb-3">
              Frequently Asked Questions
            </h2>
          </div>
          <Accordion type="single" collapsible className="space-y-3">
            {faqs.map((faq, i) => (
              <AccordionItem
                key={faq.id || i}
                value={`faq-${i}`}
                className="bg-white border border-gray-200 rounded-sm px-6"
              >
                <AccordionTrigger className="text-left font-medium text-primary hover:no-underline">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-600">{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* ─── Footer ─────────────────────────────────────────────── */}
      <footer className="bg-accent text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <h3 className="text-xl font-playfair font-bold mb-3">FB Attorneys Academy</h3>
              <p className="text-gray-300 text-sm leading-relaxed whitespace-pre-line">{config?.contact_address}</p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Contact</h4>
              <div className="space-y-2 text-sm text-gray-300">
                <div className="flex items-center gap-2"><Mail className="w-4 h-4 flex-shrink-0" /><span>{config?.contact_email}</span></div>
                <div className="flex items-center gap-2"><Phone className="w-4 h-4 flex-shrink-0" /><span>{config?.contact_phone}</span></div>
              </div>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Quick Links</h4>
              <div className="space-y-2 text-sm">
                {NAV_ITEMS.filter(n => n.id !== 'home').map(({ id, label }) => (
                  <div key={id}>
                    <button onClick={() => scrollTo(id)} className="text-gray-300 hover:text-secondary transition-colors">
                      {label}
                    </button>
                  </div>
                ))}
                <div>
                  <a href="https://fbattorneys.co.tz" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-secondary transition-colors">
                    FB Attorneys
                  </a>
                </div>
                <div>
                  <button onClick={() => navigate('/apply')} className="text-gray-300 hover:text-secondary transition-colors">Apply Now</button>
                </div>
              </div>
            </div>
          </div>
          <div className="pt-6 border-t border-gray-600 text-center text-sm text-gray-400">
            &copy; {new Date().getFullYear()} FB Attorneys Academy. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}

// ─── Defaults ───────────────────────────────────────────────
function defaultConfig() {
  return {
    logo_url: '',
    hero_title: 'Law. Technology. Leadership.',
    hero_subtitle: 'FB Attorneys Academy is a fully merit-based legal and technology hub, admitting only outstanding candidates committed to innovation, leadership, and excellence in modern legal practice.',
    hero_image_url: '',
    hero_image_mobile_url: '',
    hero_button_text: 'Apply Now',
    about_title: 'About the Academy',
    about_paragraph_1: 'FB Attorneys Academy focuses on legal training geared towards bridging the gap between academic learning and the practical demands of legal practice. The Academy equips LLB and law students with core legal skills, professional ethics, and technology-enabled competencies through structured training, hands-on workshops and simulations. Guided by the values and culture of FB Attorneys, the Academy promotes excellence, inclusion, and real-world readiness — preparing future lawyers to thrive in modern legal environments and contribute meaningfully to the profession.',
    about_paragraph_2: 'Talent should determine opportunity — not financial circumstance. FB Attorneys Academy is built on a simple principle: the most capable legal minds should have access to the tools that shape the future of law. Every admitted participant receives a full merit-based scholarship. Admission is competitive. Selection is rigorous. We invest in human capital.',
    about_paragraph_3: 'To preserve quality and impact, cohorts are intentionally small, candidates undergo structured review, selection is merit-driven, and participation requires full commitment. Excellence is our minimum standard.',
    about_image_url: '',
    features_title: 'Why Choose FB Attorneys Academy?',
    features_subtitle: 'A next generation legal and technology hub designed to equip lawyers and legal professionals with the practical, business and technological skills required to lead.',
    features: [
      { icon: 'Scale', title: 'Merit-Based Scholarship', description: 'Every admitted participant is fully funded. Admission is competitive and based on academic excellence and potential.' },
      { icon: 'Users', title: 'Expert-Led Training', description: 'Learn from FB Attorneys partners, senior associates, and guest practitioners from specialised fields around the world.' },
      { icon: 'Award', title: 'Tech-Enabled Learning', description: 'Hands-on experience with legal research platforms, e-discovery tools, contract management software and modern legal tech.' },
      { icon: 'BookOpen', title: 'Real-World Simulations', description: 'Workshops, drafting labs, litigation simulations and case strategy sessions designed around actual legal practice.' },
    ],
    program_title: 'Program Structure',
    program_subtitle: 'FB Attorneys Academy is a fully merit-based legal and technology hub, designed to bridge the gap between theory and real-world legal practice. Over 4 weeks, you will build essential soft and hard skills in legal drafting, litigation strategy and legal technology guided by seasoned attorneys and structured around hands-on simulations. We combine expert-led workshops, tech-enabled learning labs, mentorship, peer collaboration and real-case simulations to prepare you for the demands of modern legal practice.',
    program_modules: [
      { number: '01', title: 'Legal Practice', items: ['Civil litigation in action', 'Criminal law practical skills', 'Advocacy and trial skills', 'Legal drafting'] },
      { number: '02', title: 'Commercial & Regulatory', items: ['Commercial law practice', 'Tax and regulatory practice', 'Specialised practice areas', 'Research and case strategy'] },
      { number: '03', title: 'Skills & Career Development', items: ['Tech and AI for lawyers', 'Professional communication', 'Practice management', 'Career launch pad'] },
    ],
    partnerships_title: 'Partnerships & Affiliations',
    partnerships_description: 'FB Attorneys Academy is committed to building a strong ecosystem across the legal and technology landscape. We actively collaborate with industry leaders to enhance learning relevance, industry exposure, and professional opportunities for our scholars.',
    partnerships_categories: ['International law firms', 'Legal technology companies', 'Academic institutions', 'Industry professionals', 'Innovation-focused organizations', 'Regulatory agencies'],
    eligibility_criteria: [
      { text: 'Age: 25 years or below at the time of submitting this application.', active: true },
      { text: 'Academic Performance (A-Level or Equivalent): Division I in A-Level studies, or an equivalent grade in a relevant certificate or diploma programme.', active: true },
      { text: 'LLB Qualification: Holder of an LLB degree or awaiting graduation after successfully completing all coursework, with a minimum cumulative CGPA of 3.3.', active: true },
      { text: 'Year of Completion: LLB completed in 2023 or later.', active: true },
      { text: 'Admissions Assessment: Willingness to sit for an Academic Assessment on a date to be communicated by the Academy.', active: true },
    ],
    cta_title: 'Ready to Start Your Journey?',
    cta_subtitle: 'Join the next cohort and transform your legal career',
    cta_button_text: 'Apply Now',
    contact_email: 'academy@fbattorneys.co.tz',
    contact_phone: '+255 XXX XXX XXX',
    contact_address: 'FB Attorneys Academy,\n8th Floor, Amani Place,\nOhio Street, Dar es Salaam.',
    footer_description: "Training Tanzania's next generation of legal professionals through merit-based, technology-enabled legal education.",
  };
}

function defaultFaqs() {
  return [
    { question: 'Who is this programme designed for?', answer: 'The Academy is designed for final-year LLB students and recent law graduates (2023 or later) who seek to build practical, career-ready skills in legal practice and legal technology.' },
    { question: 'What is the duration and format of the programme?', answer: 'The programme runs for 4 weeks in a hybrid format, combining online expert-led sessions with in-person workshops and simulations in Dar es Salaam. The minimum commitment is 20 hours per week, totalling 80 hours.' },
    { question: 'Is there a fee to apply or participate?', answer: 'The application is free. Programme participation is fully funded by FB Attorneys through a merit-based scholarship. Specific details will be communicated upon admission.' },
    { question: 'Can I apply if I am awaiting my LLB results?', answer: 'Yes, provided you have completed all coursework and can demonstrate a minimum cumulative GPA of 3.3 (or equivalent) from your latest transcript.' },
    { question: 'What does the Academic Assessment assess?', answer: "The assessment does not test prior legal knowledge. All questions are based solely on written passages provided to the candidate. The focus is on the candidate's ability to read accurately under time pressure, identify key arguments, distinguish between fact, opinion and assumption, draw valid inferences, and interpret language with precision." },
    { question: 'What if I miss the Academic Assessment date?', answer: 'If you cannot attend, you will be deferred to the next cohort and must retake the test at that time.' },
    { question: 'What kind of tech tools will I learn to use?', answer: 'You will gain hands-on experience with legal research platforms (e.g., Manupatra, SCC Online), e-discovery tools, contract management software, and productivity apps used in modern law firms.' },
    { question: 'What is the time commitment per week?', answer: 'Minimum 20 hours per week, including live sessions, self-paced assignments, visitations and group projects.' },
    { question: 'Does the Academy offer placement assistance?', answer: 'At the moment no such placement services are offered. Graduates will gain access to the FB Attorneys Academy alumni network for continued learning and networking opportunities.' },
    { question: "Can I join the Academy's alumni network after completing the programme?", answer: 'Yes. All graduates gain access to the FB Attorneys Academy alumni network, which includes invitations to future workshops, guest lectures and networking events.' },
    { question: 'Does the Academy provide accommodation for participants?', answer: 'No. FB Attorneys Academy does not provide or arrange accommodation. The programme is non-residential. Participants are responsible for their own lodging and transportation. All training is conducted at FB Attorneys Academy, 8th Floor Amani Place, Ohio Street, Dar es Salaam.' },
    { question: 'What type of certification will I receive upon completing the programme?', answer: 'Upon successful completion of all programme requirements (including attendance and assignments), you will receive a certificate of completion from FB Attorneys Academy. This is not an academic degree or bar certification — it is designed to enhance your employability by demonstrating practical legal and technological competencies.' },
    { question: 'Can I still apply if I am currently employed or not employed?', answer: 'Yes. Your current employment status does not affect your eligibility. As long as you are 25 years of age or below at the time of application and satisfy all academic requirements, you are encouraged to apply.' },
    { question: 'What does the merit-based scholarship cover?', answer: 'The merit-based scholarship covers training costs, programme materials, certification and snacks. It does not cover travel and accommodation costs, whether within or outside of Dar es Salaam.' },
  ];
}
